import type { FC } from "react";
import { useLayoutEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { CampaignsDurationFilter } from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignsDurationFilter";
import { ModelDropdown } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/model-dropdown/ModelDropdown";
import { TopicsFilter } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/TopicsFilter";
import { extractConfigScrapeDuration } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import { proNavigator } from "single-spa/react-app/ProNavigator";

interface ILLmTrackerPageFiltersProps {
    selectedCampaign?: IAiTrackerCampaign;
    reportStatus: ReportStatus;
}

export const LLmTrackerPageFilters: FC<ILLmTrackerPageFiltersProps> = ({ selectedCampaign, reportStatus }) => {
    const {
        params: { duration, model, isDurationChanged },
        state,
    } = useCurrentStateAndParams();

    const isReportReady = reportStatus === ReportStatus.Ready;
    const { disableModelSelector } = (state.customData ?? {}) as {
        disableModelSelector?: boolean;
    };

    const [durationChangedManually, setDurationChangedManually] = useState<boolean>(false);
    const availableDurationObject: DatesRange = extractConfigScrapeDuration(selectedCampaign, model);

    const handleDurationChange = (newDuration: string): void => {
        setDurationChangedManually(isDurationChanged === "true");
        void proNavigator.updateParams({
            duration: newDuration,
            isDurationChanged: true,
        });
    };

    useLayoutEffect(() => {
        setDurationChangedManually(false);
    }, [selectedCampaign]);

    if (!duration) {
        return;
    }

    if (!selectedCampaign) {
        return (
            <PixelPlaceholderLoader
                width={200}
                height={20}
            />
        );
    }

    return (
        <Flex>
            <TopicsFilter
                isDisabled={!isReportReady}
                selectedCampaign={selectedCampaign}
            />
            <ModelDropdown
                isDisabled={disableModelSelector}
                selectedCampaign={selectedCampaign}
            />
            <CampaignsDurationFilter
                isDisabled={!selectedCampaign || !isReportReady}
                availableDurationObject={availableDurationObject}
                duration={duration as string}
                isDemo={selectedCampaign?.isDemo}
                onDurationChange={handleDurationChange}
                disableNotificationIndicator={durationChangedManually}
            />
        </Flex>
    );
};
